import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOnboarding } from '../hooks/useOnboarding';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'demo' | 'company_user' | 'company_admin' | 'super_admin';
  companyId: string | null;
}

interface OnboardingGuardProps {
  children: React.ReactNode;
}

/**
 * Route guard that enforces onboarding completion for company admins.
 *
 * Rules:
 * - Demo users (demo@securesight.ai.com) always skip onboarding
 * - Non-onboarded company admins are redirected to /onboarding
 * - Already onboarded users on /onboarding are redirected to /dashboard
 * - Public routes and auth routes are always accessible
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOnboarded, isDemo, loading, error } = useOnboarding();
  const [isChecked, setIsChecked] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  // Get user from localStorage (same pattern as ProtectedRoute)
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (err) {
        console.error('Failed to parse user data:', err);
        setUser(null);
      }
    }
  }, []);

  // Routes that don't require onboarding check
  const publicRoutes = ['/login', '/register', '/forgot-password', '/reset-password', '/onboarding'];
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

  useEffect(() => {
    if (!user || loading) return;

    // Demo users always bypass onboarding
    if (user.email === 'demo@securesight.ai.com' || isDemo) {
      setIsChecked(true);
      // If demo user is on onboarding page, redirect to dashboard
      if (location.pathname === '/onboarding') {
        navigate('/dashboard', { replace: true });
      }
      return;
    }

    // Check if user has a company
    const hasCompany = user.companyId !== null && user.companyId !== undefined;

    if (!hasCompany) {
      // Super admins and users without companies don't need onboarding
      setIsChecked(true);
      if (location.pathname === '/onboarding') {
        navigate('/dashboard', { replace: true });
      }
      return;
    }

    // Handle onboarding routes
    if (location.pathname === '/onboarding') {
      if (isOnboarded) {
        // Already onboarded, redirect to dashboard
        navigate('/dashboard', { replace: true });
      } else {
        // Not onboarded, allow access to onboarding page
        setIsChecked(true);
      }
      return;
    }

    // For protected routes, check onboarding status
    if (!isPublicRoute && !isOnboarded) {
      // Not onboarded, redirect to onboarding
      navigate('/onboarding', { replace: true });
      return;
    }

    setIsChecked(true);
  }, [loading, isOnboarded, isDemo, user, location.pathname, navigate, isPublicRoute]);

  // Show loading state while checking
  if (loading || !isChecked || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state if onboarding check failed
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-destructive">Error loading onboarding status</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render children if all checks pass
  return <>{children}</>;
}
